import { type SyntheticEvent, useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import SaveIcon from '@mui/icons-material/Save'
import type { AiDatasetStatus, AiGlobalSettings, AiModelInfo, AiProviderHealth } from 'common-billing-server/types'
import { AI_PROVIDER_LABELS } from 'common-billing-server/ai'
import { formatDistanceStrict } from 'date-fns'

import { useBillingApi } from '../../api/billing/billing-context'
import { useConfirmationDialog } from '../common/ConfirmationDialog'
import Wrapper from '../common/Wrapper'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { AiPricing } from '../settings/AiPricing'

enum AiSettingsTab {
    assistant = 'assistant',
    pricing = 'pricing',
}

const AI_SETTINGS_FIELDS = [
    'trialCreditMicroUsd',
    'monthlyBudgetMicroUsd',
    'maxJobCostMicroUsd',
    'maxLedgerEntriesPerJob',
    'anthropicTrafficPercent',
    'mantleRegion',
    'model',
    'maxTokens',
    'expertModel',
    'expertMaxTokens',
    'classifierModel',
    'classifierMaxTokens',
    'datasetAutoUpdate',
    'datasetVersion',
] as const satisfies readonly (keyof AiGlobalSettings)[]

const aiSettingsEqual = (left: AiGlobalSettings | null, right: AiGlobalSettings | null): boolean =>
    left === right ||
    (left !== null && right !== null && AI_SETTINGS_FIELDS.every((field) => left[field] === right[field]))

const changedFields = (from: AiGlobalSettings, to: AiGlobalSettings): string[] =>
    AI_SETTINGS_FIELDS.filter((field) => from[field] !== to[field]).map(
        (field) => `${field}: ${String(from[field])} → ${String(to[field])}`
    )

const formatDatasetAge = (seconds: number): string => `${formatDistanceStrict(0, Math.max(0, seconds) * 1_000)} old`

/** Names the providers exposing the selected model, so a model change shows where it can run. */
const modelProviders = (value: string, models: AiModelInfo[]): string => {
    if (!value.trim()) return 'Pick a model to see which providers expose it'
    const discovered = models.find(({ id }) => id === value)
    if (!discovered) return 'Not offered by any discovered provider'
    return `Served by ${discovered.providers.map((provider) => AI_PROVIDER_LABELS[provider]).join(', ')}`
}

const ModelField = ({
    label,
    value,
    models,
    onChange,
}: {
    label: string
    value: string
    models: AiModelInfo[]
    onChange: (value: string) => void
}) => (
    <Autocomplete
        freeSolo
        options={models.map(({ id }) => id)}
        value={value}
        onChange={(_event, selected) => onChange(selected ?? '')}
        onInputChange={(_event, input) => onChange(input)}
        renderInput={(params) => (
            <TextField {...params} label={label} required helperText={modelProviders(value, models)} />
        )}
    />
)

export const AiSettingsPage = () => {
    const billingApi = useBillingApi()
    const showConfirmation = useConfirmationDialog()
    const [settingsTab, setSettingsTab] = useState(AiSettingsTab.assistant)
    const [aiSettings, setAiSettings] = useState<AiGlobalSettings | null>(null)
    const [providerHealth, setProviderHealth] = useState<AiProviderHealth[]>([])
    const [loadedAiSettings, setLoadedAiSettings] = useState<AiGlobalSettings | null>(null)
    const [aiModels, setAiModels] = useState<AiModelInfo[]>([])
    const [datasetStatus, setDatasetStatus] = useState<AiDatasetStatus | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    const formContainsInvalidInput =
        aiSettings === null ||
        !aiSettings.mantleRegion.trim() ||
        !aiSettings.model.trim() ||
        !aiSettings.expertModel.trim() ||
        !aiSettings.classifierModel.trim() ||
        !Number.isSafeInteger(aiSettings.maxTokens) ||
        aiSettings.maxTokens <= 0 ||
        !Number.isSafeInteger(aiSettings.expertMaxTokens) ||
        aiSettings.expertMaxTokens <= 0 ||
        !Number.isSafeInteger(aiSettings.classifierMaxTokens) ||
        aiSettings.classifierMaxTokens <= 0 ||
        !Number.isSafeInteger(aiSettings.trialCreditMicroUsd) ||
        aiSettings.trialCreditMicroUsd < 0 ||
        !Number.isSafeInteger(aiSettings.anthropicTrafficPercent) ||
        aiSettings.anthropicTrafficPercent < 0 ||
        aiSettings.anthropicTrafficPercent > 100 ||
        (!aiSettings.datasetAutoUpdate && !aiSettings.datasetVersion)

    useEffect(() => {
        const operationId = 'fetch-ai-settings'

        async function fetchAiSettings() {
            removeFailedOperation(operationId)
            setIsLoading(true)
            try {
                const [settingsResult, modelsResult, datasetResult, healthResult] = await Promise.allSettled([
                    billingApi.getGlobalsettings(),
                    billingApi.listAiModels(),
                    billingApi.getAiDatasets(),
                    billingApi.listAiProviderHealths(),
                ])
                if (settingsResult.status === 'rejected') throw settingsResult.reason
                const loaded = settingsResult.value.ai ?? null
                setAiSettings(loaded)
                setLoadedAiSettings(loaded)
                if (!loaded) {
                    addFailedOperation({
                        id: operationId,
                        message: 'Global AI settings are unavailable',
                    })
                }

                if (modelsResult.status === 'fulfilled') {
                    setAiModels(modelsResult.value)
                } else {
                    addFailedOperation({
                        id: 'fetch-ai-models',
                        message: 'Failed fetching available AI models',
                        error: modelsResult.reason,
                    })
                }
                if (datasetResult.status === 'fulfilled') {
                    setDatasetStatus(datasetResult.value)
                } else {
                    addFailedOperation({
                        id: 'fetch-ai-datasets',
                        message: 'Failed fetching available AI datasets',
                        error: datasetResult.reason,
                    })
                }
                if (healthResult.status === 'fulfilled') {
                    setProviderHealth(healthResult.value)
                } else {
                    addFailedOperation({
                        id: 'fetch-ai-provider-health',
                        message: 'Failed fetching AI provider health',
                        error: healthResult.reason,
                    })
                }
            } catch (error) {
                addFailedOperation({
                    id: operationId,
                    message: 'Failed fetching AI settings',
                    error,
                })
            } finally {
                setIsLoading(false)
            }
        }
        void fetchAiSettings()
    }, [billingApi])

    const saveAiSettings = async () => {
        if (formContainsInvalidInput || !aiSettings || !loadedAiSettings) return
        const operationId = 'update-ai-settings'
        removeFailedOperation(operationId)
        setIsLoading(true)
        try {
            const currentSettings = await billingApi.getGlobalsettings()
            const serverAiSettings = currentSettings.ai ?? null
            if (!aiSettingsEqual(serverAiSettings, loadedAiSettings)) {
                setAiSettings(serverAiSettings)
                setLoadedAiSettings(serverAiSettings)
                addFailedOperation({
                    id: operationId,
                    message:
                        'Another administrator changed the AI settings while this page was open. ' +
                        'The form has been reloaded — review and apply your change again.',
                })
                return
            }
            const saved = await billingApi.setGlobalsettings({
                logLevel: currentSettings.logLevel,
                mfaEnforced: currentSettings.mfaEnforced,
                ai: aiSettings,
            })
            const savedAiSettings = saved.ai ?? null
            setAiSettings(savedAiSettings)
            setLoadedAiSettings(savedAiSettings)
            setDatasetStatus(await billingApi.getAiDatasets())
        } catch (error) {
            addFailedOperation({
                id: operationId,
                message: 'Failed updating AI settings',
                error,
            })
        } finally {
            setIsLoading(false)
        }
    }

    const confirmAndSave = (event: SyntheticEvent) => {
        event.preventDefault()
        if (formContainsInvalidInput || !aiSettings || !loadedAiSettings) return
        const changes = changedFields(loadedAiSettings, aiSettings)
        if (changes.length === 0) return
        showConfirmation(
            () => void saveAiSettings(),
            <Box>
                <Typography>Apply to every installation and customer:</Typography>
                {changes.map((change) => (
                    <Typography key={change} component="div">
                        {change}
                    </Typography>
                ))}
            </Box>,
            { ok: { text: 'Update', startIcon: <SaveIcon /> } }
        )
    }

    return (
        <Wrapper>
            <Paper>
                <Tabs value={settingsTab} variant="fullWidth" onChange={(_event, value) => setSettingsTab(value)}>
                    <Tab label="Assistant" value={AiSettingsTab.assistant} />
                    <Tab label="Pricing" value={AiSettingsTab.pricing} />
                </Tabs>
                {settingsTab === AiSettingsTab.assistant ? (
                    <form onSubmit={confirmAndSave}>
                        <Grid container spacing={2} sx={{ p: 2, justifyContent: 'flex-start', alignItems: 'center' }}>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="h6">AI assistant</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={aiSettings?.datasetAutoUpdate ?? true}
                                            onChange={(_event, checked) =>
                                                setAiSettings((current) =>
                                                    current
                                                        ? {
                                                              ...current,
                                                              datasetAutoUpdate: checked,
                                                              datasetVersion:
                                                                  current.datasetVersion ??
                                                                  datasetStatus?.effectiveVersion,
                                                          }
                                                        : current
                                                )
                                            }
                                        />
                                    }
                                    label="Automatic dataset updates"
                                />
                                {datasetStatus ? (
                                    datasetStatus.servingIsFallback ? (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            Dataset fallback active: serving {datasetStatus.servingVersion.slice(0, 12)}{' '}
                                            ({formatDatasetAge(datasetStatus.servingAgeSeconds)}) instead of selected{' '}
                                            {datasetStatus.effectiveVersion.slice(0, 12)}.
                                        </Alert>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Serving {datasetStatus.servingVersion.slice(0, 12)} (
                                            {formatDatasetAge(datasetStatus.servingAgeSeconds)}; automatic offset{' '}
                                            {datasetStatus.autoOffset})
                                        </Typography>
                                    )
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Dataset status unavailable
                                    </Typography>
                                )}
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Autocomplete
                                    disabled={aiSettings?.datasetAutoUpdate ?? true}
                                    options={datasetStatus?.availableVersions ?? []}
                                    value={
                                        datasetStatus?.availableVersions.find(
                                            ({ version }) => version === aiSettings?.datasetVersion
                                        ) ?? null
                                    }
                                    getOptionLabel={({ version, createdAt }) =>
                                        `${version.slice(0, 12)} — ${new Date(createdAt).toLocaleString()}`
                                    }
                                    isOptionEqualToValue={(option, value) => option.version === value.version}
                                    onChange={(_event, selected) =>
                                        setAiSettings((current) =>
                                            current ? { ...current, datasetVersion: selected?.version } : current
                                        )
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Pinned dataset version"
                                            required={!aiSettings?.datasetAutoUpdate}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Trial credit (micro-USD)"
                                    type="number"
                                    value={aiSettings?.trialCreditMicroUsd ?? ''}
                                    onChange={(event) =>
                                        setAiSettings((current) =>
                                            current
                                                ? { ...current, trialCreditMicroUsd: Number(event.target.value) }
                                                : current
                                        )
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Anthropic traffic (%)"
                                    type="number"
                                    helperText="Share of new assistant jobs sent to the direct Anthropic API; the rest go to Bedrock Mantle."
                                    value={aiSettings?.anthropicTrafficPercent ?? ''}
                                    onChange={(event) =>
                                        setAiSettings((current) =>
                                            current
                                                ? { ...current, anthropicTrafficPercent: Number(event.target.value) }
                                                : current
                                        )
                                    }
                                />
                            </Grid>
                            {providerHealth.length > 0 ? (
                                <Grid size={{ xs: 12 }} data-testid="ai-provider-health">
                                    <Typography variant="subtitle2">Provider availability</Typography>
                                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                        {providerHealth.map((health) => (
                                            <Chip
                                                key={health.provider}
                                                size="small"
                                                color={
                                                    !health.registered
                                                        ? 'default'
                                                        : health.circuitOpen
                                                          ? 'error'
                                                          : 'success'
                                                }
                                                variant={health.registered ? 'filled' : 'outlined'}
                                                label={`${AI_PROVIDER_LABELS[health.provider]}: ${
                                                    !health.registered
                                                        ? 'no credentials'
                                                        : health.circuitOpen
                                                          ? 'circuit open'
                                                          : 'available'
                                                } · ${health.recentFailures}/${health.recentCalls} failed in ${health.windowSeconds}s`}
                                            />
                                        ))}
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        An open circuit carries no new jobs until a leased recovery call succeeds.
                                    </Typography>
                                </Grid>
                            ) : null}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Mantle region"
                                    value={aiSettings?.mantleRegion ?? ''}
                                    onChange={(event) =>
                                        setAiSettings((current) =>
                                            current ? { ...current, mantleRegion: event.target.value } : current
                                        )
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <ModelField
                                    label="Assistant model"
                                    value={aiSettings?.model ?? ''}
                                    models={aiModels}
                                    onChange={(model) =>
                                        setAiSettings((current) => (current ? { ...current, model } : current))
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Maximum output tokens"
                                    type="number"
                                    value={aiSettings?.maxTokens ?? ''}
                                    onChange={(event) =>
                                        setAiSettings((current) =>
                                            current ? { ...current, maxTokens: Number(event.target.value) } : current
                                        )
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <ModelField
                                    label="Product expert model"
                                    value={aiSettings?.expertModel ?? ''}
                                    models={aiModels}
                                    onChange={(expertModel) =>
                                        setAiSettings((current) => (current ? { ...current, expertModel } : current))
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Product expert maximum output tokens"
                                    type="number"
                                    value={aiSettings?.expertMaxTokens ?? ''}
                                    onChange={(event) =>
                                        setAiSettings((current) =>
                                            current
                                                ? { ...current, expertMaxTokens: Number(event.target.value) }
                                                : current
                                        )
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <ModelField
                                    label="Policy classifier model"
                                    value={aiSettings?.classifierModel ?? ''}
                                    models={aiModels}
                                    onChange={(classifierModel) =>
                                        setAiSettings((current) =>
                                            current ? { ...current, classifierModel } : current
                                        )
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Policy classifier maximum output tokens"
                                    type="number"
                                    value={aiSettings?.classifierMaxTokens ?? ''}
                                    onChange={(event) =>
                                        setAiSettings((current) =>
                                            current
                                                ? { ...current, classifierMaxTokens: Number(event.target.value) }
                                                : current
                                        )
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={formContainsInvalidInput || isLoading}
                                        startIcon={
                                            isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                                        }
                                    >
                                        Update
                                    </Button>
                                </Box>
                            </Grid>
                            {errorBanner}
                        </Grid>
                    </form>
                ) : null}
                {settingsTab === AiSettingsTab.pricing ? <AiPricing models={aiModels} /> : null}
            </Paper>
        </Wrapper>
    )
}
